import { Plus } from "lucide-react";
import type { Team } from "../lib/types";
import { cn } from "../lib/utils";

export function TeamDock({
  teams,
  currentUserId,
  activeTeamId,
  activeView,
  label,
  canCreate,
  onCreate,
  onSelectTeam,
}: {
  teams: Team[];
  currentUserId: string | null;
  activeTeamId: string | null;
  activeView: string;
  label: string;
  canCreate: boolean;
  onCreate: () => void;
  onSelectTeam: (teamId: string, view: string) => void;
}) {
  return (
    <div className="shrink-0 border-t border-[var(--border)] px-2 py-2">
      <div className="group flex items-center justify-between px-2 pb-1">
        <span className="text-[10.5px] font-medium uppercase tracking-wide text-[var(--fg-muted)]">
          {label}
        </span>
        {canCreate && (
          <button
            onClick={onCreate}
            className="flex h-4 w-4 items-center justify-center rounded text-[var(--fg-tertiary)] opacity-0 transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg-secondary)] group-hover:opacity-100 focus-visible:opacity-100"
            title={label === "Areas" ? "New area" : "New team"}
            aria-label={label === "Areas" ? "New area" : "New team"}
          >
            <Plus size={11} />
          </button>
        )}
      </div>
      <div className="flex max-h-[148px] flex-col gap-0.5 overflow-y-auto scrollbar-thin">
        {teams.map((team) => {
          const isMember = !!currentUserId && team.memberIds.includes(currentUserId);
          const isActive =
            activeView === `team:${team.id}` ||
            (["all", "active", "backlog", "board"].includes(activeView) &&
              activeTeamId === team.id);
          const membershipLabel = isMember
            ? team.name
            : `${team.name}, not on this team`;

          return (
            <button
              key={team.id}
              type="button"
              onClick={() => onSelectTeam(team.id, "team")}
              data-team-membership={isMember ? "member" : "nonmember"}
              aria-label={membershipLabel}
              aria-current={isActive ? "page" : undefined}
              title={isMember ? team.name : `${team.name} · Not on this team`}
              className={cn(
                "group flex w-full items-center gap-2 rounded-md px-2 py-1 text-[12.5px] transition-colors",
                isActive ? "bg-[var(--bg-active)]" : "hover:bg-[var(--bg-hover)]",
                isMember
                  ? isActive
                    ? "text-[var(--fg)]"
                    : "text-[var(--fg-secondary)] hover:text-[var(--fg)]"
                  : "text-[var(--fg-muted)] hover:text-[var(--fg-tertiary)]"
              )}
            >
              <span
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] text-[10px]",
                  !isMember && "grayscale opacity-40 group-hover:opacity-70"
                )}
                style={{ background: `${team.color}22`, color: team.color }}
                aria-hidden="true"
              >
                {team.icon}
              </span>
              <span className="min-w-0 flex-1 truncate text-left font-medium">
                {team.name}
              </span>
              {!isMember && <span className="sr-only">Not on this team</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
